import type { StaticImageData } from "next/image";

import nearLogo from "@public/chains/near.svg";
import ethereumLogo from "@public/chains/ethereum.svg";
import bscLogo from "@public/chains/bsc.svg";
import arbitrumLogo from "@public/chains/arbitrum.svg";
import baseLogo from "@public/chains/base.svg";

export interface ChainConfig {
  /**
   * URL slug for `/supported-chains/<id>` and the `ChainPage.<id>` translation
   * namespace. Stable: retired top-level routes redirect onto it.
   */
  id: string;
  name: string;
  symbol: string;
  logo: StaticImageData;
  /**
   * Optical size correction for the logo, multiplied onto its rendered box.
   * The source SVGs have very different viewBoxes and glyph-fill ratios (e.g.
   * NEAR fills its 42×42 box edge-to-edge while Ethereum's diamond is a tall
   * 256×417), so `object-fit: contain` alone renders them at unequal visual
   * sizes. Tune these so every chain reads as the same size. Defaults to 1.
   */
  logoScale?: number;
  accentColor: string;
  learnMoreUrl: string;
}

/** Order here drives the sidebar and the index page listing. */
export const SUPPORTED_CHAINS: ChainConfig[] = [
  {
    id: "near",
    name: "NEAR",
    symbol: "NEAR",
    logo: nearLogo,
    logoScale: 0.88,
    accentColor: "#00EC97",
    learnMoreUrl: "https://near.org",
  },
  {
    id: "ethereum",
    name: "Ethereum",
    symbol: "ETH",
    logo: ethereumLogo,
    logoScale: 1.05,
    accentColor: "#627EEA",
    learnMoreUrl: "https://ethereum.org",
  },
  {
    id: "bsc",
    name: "BSC",
    symbol: "BNB",
    logo: bscLogo,
    logoScale: 1.0,
    accentColor: "#F0B90B",
    learnMoreUrl: "https://www.bnbchain.org",
  },
  {
    id: "arbitrum",
    name: "Arbitrum",
    symbol: "ARB",
    logo: arbitrumLogo,
    logoScale: 1.0,
    accentColor: "#12AAFF",
    learnMoreUrl: "https://arbitrum.io",
  },
  {
    id: "base",
    name: "Base",
    symbol: "BASE",
    logo: baseLogo,
    logoScale: 1.0,
    accentColor: "#0052FF",
    learnMoreUrl: "https://www.base.org",
  },
];

export const SUPPORTED_CHAINS_PATH = "/supported-chains";

export const getChainConfig = (id: string): ChainConfig | undefined =>
  SUPPORTED_CHAINS.find((c) => c.id === id);

export const getChainPath = (id: string): string =>
  `${SUPPORTED_CHAINS_PATH}/${id}`;
